const STAGES = [
  { n: "01", t: "Company", checks: ["Headcount", "Geography", "Industry", "Suppression — Client"] },
  { n: "02", t: "Contact", checks: ["Location · Function", "Dual-employment", "Open-to-work", "Tenure"] },
  { n: "03", t: "Email · Phone", checks: ["Syntax · MX · Deliverability", "Duplicate check", "Suppression — Campaign", "Suppression — Agency"] },
  { n: "04", t: "Lead File", checks: ["Field completeness", "Format compliance", "Delivery template match", "Client suppression"] },
];

export default function LeadAudit() {
  return (
    <section id="quality" data-testid="lead-audit-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 items-end">
          <div className="lg:col-span-6">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
              Lead Quality · Audit Engine v2.0
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[56px] leading-[1] tracking-[-0.02em]">
              18 checks. Four stages. <span className="italic font-normal text-teal">Zero surprises.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="text-base text-muted-foreground max-w-md">
              Every lead audited before delivery — AI-validated in real time,
              verified against ground truth, and matched to your suppression rules.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {STAGES.map((s) => (
            <div key={s.n} className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6 card-hover">
              <div className="flex items-center justify-between mb-6">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Stage {s.n}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-teal">4 checks</div>
              </div>
              <h3 className="font-display text-xl font-semibold mb-4">{s.t}</h3>
              <ul className="space-y-2">
                {s.checks.map((c) => (
                  <li key={c} className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-teal" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Dashboard snapshot */}
        <div className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-8 md:p-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-teal">Live Dashboard · Q2 2026</div>
              <div className="font-display text-2xl md:text-3xl font-semibold mt-2">LeadAudit AI — snapshot.</div>
            </div>
            <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground font-mono">
              <span className="h-1.5 w-1.5 rounded-full bg-teal pulse-dot" />
              Refreshed 2m ago
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { v: "77,857", l: "Total Processed", s: "Leads audited this quarter" },
              { v: "78.4%", l: "Pass Rate", s: "61,040 leads passed" },
              { v: "1.8s", l: "Avg Audit Time", s: "Per lead, fully automated" },
              { v: "96.1%", l: "Data Accuracy", s: "Verified vs ground truth" },
            ].map((k) => (
              <div key={k.l} className="rounded-xl border border-white/5 p-5">
                <div className="font-display text-3xl md:text-4xl font-semibold">{k.v}</div>
                <div className="text-xs text-teal mt-2 font-mono uppercase tracking-[0.14em]">{k.l}</div>
                <div className="text-xs text-muted-foreground mt-1">{k.s}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-4">Global lead distribution</div>
              {[
                { k: "EMEA", n: "32,700", w: "42%" },
                { k: "N. America", n: "24,136", w: "31%" },
                { k: "APAC", n: "14,793", w: "19%" },
                { k: "LATAM", n: "6,228", w: "8%" },
              ].map((r) => (
                <div key={r.k} className="mb-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span>{r.k}</span>
                    <span className="font-mono text-muted-foreground">{r.n} · {r.w}</span>
                  </div>
                  <div className="h-1 rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-teal/70" style={{ width: r.w }} />
                  </div>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-4">Suppression breakdown · blocked at intake</div>
              <div className="rounded-xl border border-white/5 p-5">
                <div className="font-display text-3xl font-semibold">16,817</div>
                <div className="text-xs text-muted-foreground mt-1">Total blocked</div>
                <div className="mt-5 space-y-2">
                  {[
                    { k: "Campaign", v: "7,736", w: "46%" },
                    { k: "Agency", v: "5,381", w: "32%" },
                    { k: "Client", v: "3,700", w: "22%" },
                  ].map((s) => (
                    <div key={s.k} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{s.k}</span>
                      <span className="font-mono">{s.v} <span className="text-teal">{s.w}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
